import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Moon, Sun, Menu, X, ArrowUpRight, User, LogOut, Shield, LayoutDashboard } from "lucide-react";
import { useTheme } from "@/lib/theme";
import { useAuth } from "@/lib/auth";
import { NAV_LINKS } from "@/lib/constants";
import { cn } from "@/lib/utils";

export default function Navbar() {
  const { theme, toggle } = useTheme();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (!e.target.closest("[data-account-menu]")) setAccountOpen(false);
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  const onLogout = async () => {
    await logout();
    setAccountOpen(false);
    navigate("/");
  };

  return (
    <header
      data-testid="site-navbar"
      className={cn(
        "fixed top-0 inset-x-0 z-50 transition-all duration-300",
        scrolled ? "backdrop-blur-xl bg-background/75 border-b border-border" : "bg-transparent"
      )}
    >
      <div className="container-x flex items-center justify-between h-16 md:h-20">
        <Link to="/" data-testid="nav-logo-link" className="flex items-center gap-3 group">
          <span className="relative inline-flex h-8 w-8 items-center justify-center rounded-md border border-border bg-card">
            <span className="absolute inset-1 rounded-sm bg-[hsl(var(--accent))]/15" />
            <span className="relative font-display text-[13px] font-semibold tracking-tight text-[hsl(var(--accent))]">C</span>
          </span>
          <div className="leading-tight">
            <div className="font-display text-sm md:text-[15px] font-semibold tracking-tight">The Cleanability Institute</div>
            <div className="font-mono-mini text-[10px] text-muted-foreground hidden md:block">Est. 2024 · Global</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV_LINKS.slice(0, 7).map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              data-testid={`nav-link-${link.to.replace("/", "")}`}
              className={({ isActive }) =>
                cn(
                  "text-[13px] font-medium tracking-tight transition-colors",
                  isActive ? "text-foreground" : "text-muted-foreground hover:text-foreground"
                )
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <button
            onClick={toggle}
            data-testid="theme-toggle"
            aria-label="Toggle theme"
            className="h-9 w-9 inline-flex items-center justify-center rounded-md border border-border hover:bg-secondary transition-colors"
          >
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>

          {user ? (
            <div className="relative" data-account-menu>
              <button
                onClick={(e) => { e.stopPropagation(); setAccountOpen(!accountOpen); }}
                data-testid="account-menu-button"
                className="hidden md:inline-flex items-center gap-2 h-9 px-3 rounded-md border border-border hover:bg-secondary text-sm"
              >
                <span className="h-6 w-6 rounded-full bg-[hsl(var(--accent))]/15 grid place-items-center text-[hsl(var(--accent))] text-[10px] font-display font-semibold">
                  {(user.name || user.email || "?")[0].toUpperCase()}
                </span>
                <span className="font-medium">{user.name?.split(" ")[0] || "Account"}</span>
              </button>
              {accountOpen && (
                <div data-testid="account-menu" className="absolute right-0 mt-2 w-56 rounded-xl border border-border bg-card shadow-lg overflow-hidden">
                  <div className="p-3 border-b border-border">
                    <div className="text-xs text-muted-foreground">Signed in as</div>
                    <div className="text-sm font-medium truncate">{user.email}</div>
                    <div className="text-[10px] font-mono-mini text-[hsl(var(--accent))] mt-1 uppercase">{user.role} · {user.tier || "free"}</div>
                  </div>
                  <Link to="/dashboard" onClick={() => setAccountOpen(false)} data-testid="account-dashboard" className="flex items-center gap-2 px-3 py-2.5 text-sm hover:bg-secondary">
                    <LayoutDashboard className="h-4 w-4" /> Dashboard
                  </Link>
                  {user.role === "admin" && (
                    <Link to="/admin" onClick={() => setAccountOpen(false)} data-testid="account-admin" className="flex items-center gap-2 px-3 py-2.5 text-sm hover:bg-secondary">
                      <Shield className="h-4 w-4" /> Admin panel
                    </Link>
                  )}
                  <button onClick={onLogout} data-testid="account-logout" className="w-full flex items-center gap-2 px-3 py-2.5 text-sm hover:bg-secondary text-left">
                    <LogOut className="h-4 w-4" /> Sign out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login" className="hidden md:inline-flex items-center gap-1.5 text-[13px] font-medium px-3 h-9 rounded-md text-foreground/80 hover:text-foreground">
                <User className="h-3.5 w-3.5" /> Sign in
              </Link>
              <Link to="/contact" data-testid="nav-cta-contact" className="hidden md:inline-flex items-center gap-1.5 text-[13px] font-medium px-4 h-9 rounded-md bg-foreground text-background hover:bg-foreground/90 transition-colors">
                Enquire <ArrowUpRight className="h-3.5 w-3.5" />
              </Link>
            </>
          )}

          <button
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            className="lg:hidden h-9 w-9 inline-flex items-center justify-center rounded-md border border-border"
            aria-label="Menu"
          >
            {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden border-t border-border bg-background/95 backdrop-blur-xl">
          <div className="container-x py-6 grid grid-cols-2 gap-x-6 gap-y-3">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${link.to.replace("/", "")}`}
                className="py-2 text-sm font-medium text-foreground/90 hover:text-[hsl(var(--accent))]"
              >
                {link.label}
              </NavLink>
            ))}
            {user ? (
              <>
                <Link to="/dashboard" onClick={() => setOpen(false)} data-testid="mobile-dashboard" className="py-2 text-sm font-medium">Dashboard</Link>
                {user.role === "admin" && <Link to="/admin" onClick={() => setOpen(false)} data-testid="mobile-admin" className="py-2 text-sm font-medium">Admin</Link>}
                <button onClick={onLogout} data-testid="mobile-logout" className="col-span-2 mt-4 inline-flex items-center justify-center gap-1.5 text-sm font-medium px-4 h-10 rounded-md border border-border">
                  Sign out
                </button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} data-testid="mobile-login" className="py-2 text-sm font-medium">Sign in</Link>
                <Link to="/contact" onClick={() => setOpen(false)} data-testid="mobile-cta" className="col-span-2 mt-4 inline-flex items-center justify-center gap-1.5 text-sm font-medium px-4 h-10 rounded-md bg-foreground text-background">
                  Enquire <ArrowUpRight className="h-4 w-4" />
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
